import { Injectable } from '@angular/core';
import {
  Subject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  filter,
  startWith,
} from 'rxjs';
import { isEqual } from 'lodash';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { SpaceRepository } from '../../../state/space.repository';
import { SpaceBoardsService } from '../../../services/space-boards.service';

export interface InteractionsLockState {
  interactionsLockEnabled: boolean;
  interactionsDisabledForUser: boolean;
  isSpaceOrBoardLocked: boolean;
  canToggleLock: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class AppsInteractionsLockService {
  private readonly interactionsLocked$ = new Subject<{
    interactionsLocked: boolean;
    itemId: string;
  }>();
  private readonly isSpaceOrBoardLocked$;

  constructor(
    private flagsService: FlagsService,
    private spaceRepo: SpaceRepository,
    private spaceBoardsService: SpaceBoardsService,
  ) {
    this.isSpaceOrBoardLocked$ = combineLatest([
      this.spaceRepo.activeSpaceLocked$,
      this.spaceBoardsService.currentBoardLocked$,
    ]).pipe(
      map(([spaceLocked, boardLocked]) => Boolean(spaceLocked || boardLocked)),
      distinctUntilChanged(),
    );
  }

  public interactionsLockState$(
    itemId: string,
    initialInteractionsLocked?: boolean,
  ): Observable<InteractionsLockState> {
    return combineLatest<[boolean, boolean, boolean, boolean]>([
      this.flagsService.featureFlagChanged(FLAGS.APPS_INTERACTIONS_LOCK_SWITCH),
      this.interactionsLocked$.pipe(
        filter(({ itemId: currentItemId }) => currentItemId === itemId),
        map(({ interactionsLocked }) => interactionsLocked),
        initialInteractionsLocked !== undefined
          ? (source) => source.pipe(startWith(initialInteractionsLocked))
          : (source) => source,
      ),
      this.isSpaceOrBoardLocked$,
      this.spaceRepo.isCurrentUserHost$,
    ]).pipe(
      map(
        ([
          interactionsLockSwitchFeatureEnabled,
          interactionsLocked,
          isSpaceOrBoardLocked,
          isCurrentUserHost,
        ]): InteractionsLockState => {
          const interactionsLockEnabled =
            interactionsLockSwitchFeatureEnabled && (interactionsLocked || isSpaceOrBoardLocked);
          return {
            interactionsLockEnabled,
            interactionsDisabledForUser: interactionsLockEnabled && !isCurrentUserHost,
            isSpaceOrBoardLocked,
            canToggleLock: isCurrentUserHost && interactionsLockSwitchFeatureEnabled,
          };
        },
      ),
      distinctUntilChanged(isEqual),
    );
  }

  public onInteractionsLockChanged(interactionsLocked: boolean, itemId: string) {
    this.interactionsLocked$.next({ interactionsLocked, itemId });
  }
}
